import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { auth } from './Firebase';
import { useAuth } from '../contexts/AuthContext';

const planLabels = {
  plus: 'Symuliga Plus',
  regions: 'Symuliga Regionalna',
};

const intervalLabels = {
  monthly: 'miesięcznie',
  yearly: 'rocznie',
};

const normalizeInterval = (value) => (value === 'yearly' ? 'yearly' : 'monthly');

export default function BillingCheckoutPage() {
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const plan = useMemo(() => searchParams.get('plan') || 'plus', [searchParams]);
  const interval = useMemo(
    () => normalizeInterval(searchParams.get('interval')),
    [searchParams]
  );
  const isUpgrade = useMemo(() => searchParams.get('upgrade') === 'true', [searchParams]);
  const startedRef = useRef(false);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);

  const startCheckout = useCallback(async () => {
    setError(null);
    setStarting(true);

    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('Musisz się zalogować, żeby kupić dostęp.');
      }

      if (isUpgrade) {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/billing/upgrade-subscription`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.data?.url) {
          throw new Error('Stripe nie zwrócił linku do rozszerzenia pakietu.');
        }

        window.location.assign(response.data.url);
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/billing/checkout-session`,
        { plan, interval },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.data?.url) {
        throw new Error('Stripe nie zwrócił linku do płatności.');
      }

      window.location.assign(response.data.url);
    } catch (err) {
      const message = err.response?.data?.message || err.response?.data?.error || err.message;
      setError(message || 'Nie udało się rozpocząć płatności.');
      setStarting(false);
      startedRef.current = false;
    }
  }, [plan, interval, isUpgrade]);

  useEffect(() => {
    if (loading || !user || startedRef.current) {
      return;
    }
    startedRef.current = true;
    startCheckout();
  }, [loading, user, startCheckout]);

  if (loading) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Paper sx={{ maxWidth: 560, mx: 'auto', my: 6, p: 4, textAlign: 'center' }}>
        <Typography variant="h5" fontWeight={900} gutterBottom>
          Zaloguj się, żeby kupić {planLabels[plan] || 'pakiet'}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Płatność musi być przypisana do konta, żeby automatycznie nadać dostęp.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
          <Button
            component={RouterLink}
            to={`/login?redirect=${encodeURIComponent(
              isUpgrade
                ? `/kup-vip?plan=${plan}&upgrade=true`
                : `/kup-vip?plan=${plan}&interval=${interval}`
            )}`}
            variant="contained"
          >
            Zaloguj się
          </Button>
          <Button component={RouterLink} to="/register" variant="outlined">
            Załóż konto
          </Button>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper sx={{ maxWidth: 560, mx: 'auto', my: 6, p: 4, textAlign: 'center' }}>
      <Typography variant="h5" fontWeight={900} gutterBottom>
        {isUpgrade ? 'Rozszerzam pakiet' : 'Przygotowuję płatność Stripe'}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {isUpgrade
          ? 'Zmieniam aktywną subskrypcję Plus na Symuliga Regionalna.'
          : `Pakiet: ${planLabels[plan] || plan}, ${intervalLabels[interval]}`}
      </Typography>
      {starting && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <CircularProgress />
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
          {error}
        </Alert>
      )}
      <Button variant="contained" onClick={startCheckout} disabled={starting}>
        Spróbuj ponownie
      </Button>
    </Paper>
  );
}
